import React, { useMemo } from "react";
import { motion } from "framer-motion";

interface AnimatedNumberProps {
  /** Valor a mostrar. Al cambiar, cada dígito rueda hasta su nueva cifra. */
  value: number;
  /**
   * Formatea el valor a texto. Por defecto entero locale es-CL. Los dígitos del
   * resultado ruedan; el resto de caracteres (`.` `,` `%` espacios) quedan fijos.
   * Ej. 2 decimales: (n) => n.toFixed(2).replace(".", ",")
   */
  format?: (value: number) => string;
  /** Duración (s) del giro de las ruedas. */
  duration?: number;
}

// Formateador entero por defecto (locale es-CL, sin decimales).
const defaultFormat = (value: number): string =>
  Math.round(value).toLocaleString("es-CL");

const DIGITS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

// easeOutExpo: arranca rápido y "asienta" al final, como una rueda mecánica.
const REEL_EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

const cellStyle: React.CSSProperties = {
  display: "block",
  height: "1em",
  lineHeight: 1,
  textAlign: "center",
};

/**
 * Un dígito = un carril vertical con las cifras 0-9 apiladas (10em de alto). La
 * ventana mide 1em con overflow oculto; se traslada en Y para dejar visible la
 * cifra objetivo. Al montar parte de 0 (efecto surtidor); al cambiar, rueda
 * desde la cifra actual hasta la nueva (arriba si sube, abajo si baja).
 */
const DigitReel = ({ digit, duration }: { digit: number; duration: number }) => (
  <span
    style={{
      display: "inline-block",
      overflow: "hidden",
      height: "1em",
      lineHeight: 1,
      verticalAlign: "bottom",
    }}
  >
    <motion.span
      style={{ display: "block", willChange: "transform" }}
      initial={{ y: "0%" }}
      animate={{ y: `${digit * -10}%` }}
      transition={{ duration, ease: REEL_EASE }}
    >
      {DIGITS.map((d) => (
        <span key={d} style={cellStyle}>
          {d}
        </span>
      ))}
    </motion.span>
  </span>
);

/**
 * Número con animación tipo odómetro / surtidor de bencina: cada dígito es una
 * rueda independiente que gira hasta su cifra. Funciona sobre el texto ya
 * formateado, así que respeta miles, decimales y el signo `%`.
 */
const AnimatedNumber = ({
  value,
  format = defaultFormat,
  duration = 0.85,
}: AnimatedNumberProps) => {
  const chars = useMemo(() => format(value).split(""), [format, value]);

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "flex-end",
        fontVariantNumeric: "tabular-nums",
        verticalAlign: "bottom",
      }}
    >
      {chars.map((ch, index) =>
        ch >= "0" && ch <= "9" ? (
          <DigitReel key={index} digit={Number(ch)} duration={duration} />
        ) : (
          <span
            key={index}
            style={{ display: "inline-block", height: "1em", lineHeight: 1, whiteSpace: "pre" }}
          >
            {ch}
          </span>
        ),
      )}
    </span>
  );
};

export default AnimatedNumber;
